import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Save, Link2, Copy, RefreshCw, ExternalLink, RotateCw } from "lucide-react";

const MONTHS = ["Jan", "Fév", "Mar", "Avr", "Mai", "Jun", "Jul", "Aoû", "Sep", "Oct", "Nov", "Déc"];
const WEEKDAYS = ["L", "M", "M", "J", "V", "S", "D"];

function ymd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }

export default function CalendarManager({ listingId, basePrice }) {
  const today = new Date();
  const [cursor, setCursor] = useState(startOfMonth(today));
  const [overrides, setOverrides] = useState({});
  const [bookedDates, setBookedDates] = useState(new Set());
  const [pending, setPending] = useState({});
  const [bulkMode, setBulkMode] = useState("toggle"); // toggle | price
  const [bulkPrice, setBulkPrice] = useState(basePrice || 0);
  const [saving, setSaving] = useState(false);
  const [icalInfo, setIcalInfo] = useState(null);
  const [icalUrls, setIcalUrls] = useState("");
  const [syncing, setSyncing] = useState(false);

  const loadIcal = async () => {
    try {
      const { data } = await api.get(`/listings/${listingId}/ical-info`);
      setIcalInfo(data);
      setIcalUrls((data.external_ical_urls || []).join("\n"));
    } catch (_) {}
  };

  const load = async () => {
    const start = ymd(startOfMonth(cursor));
    const end = ymd(new Date(cursor.getFullYear(), cursor.getMonth() + 3, 0));
    const { data } = await api.get(`/listings/${listingId}/calendar`, { params: { start, end } });
    const map = {};
    for (const o of data.overrides) map[o.date] = o;
    setOverrides(map);
    setBookedDates(new Set(data.booked_dates || []));
  };

  useEffect(() => { load(); }, [listingId, cursor]);
  useEffect(() => { loadIcal(); }, [listingId]);

  const exportUrl = icalInfo
    ? `${process.env.REACT_APP_BACKEND_URL}${icalInfo.export_path}`
    : "";

  const copyExport = async () => {
    if (!exportUrl) return;
    await navigator.clipboard.writeText(exportUrl);
    toast.success("Lien iCal copié");
  };

  const syncIcal = async () => {
    const urls = icalUrls.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
    setSyncing(true);
    try {
      const { data } = await api.post(`/listings/${listingId}/ical/import`, { urls });
      const errors = data.errors || [];
      if (errors.length) {
        toast.warning(`${data.dates_blocked} dates bloquées · ${errors.length} URL(s) en erreur`);
      } else {
        toast.success(`Synchronisation terminée : ${data.dates_blocked} dates bloquées`);
      }
      loadIcal();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSyncing(false);
    }
  };

  const rotateToken = async () => {
    if (!window.confirm("Régénérer le lien iCal d'export ?\nL'ancien lien deviendra invalide. Vous devrez le mettre à jour sur Airbnb / Booking / Abritel.")) return;
    try {
      const { data } = await api.post(`/listings/${listingId}/ical-info/rotate`);
      setIcalInfo({ ...icalInfo, ical_token: data.ical_token, export_path: data.export_path });
      toast.success("Lien iCal régénéré — pensez à mettre à jour vos plateformes externes.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const formatLastSync = (iso) => {
    if (!iso) return null;
    try {
      const d = new Date(iso);
      return d.toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
    } catch { return null; }
  };

  const days = useMemo(() => {
    const arr = [];
    const first = startOfMonth(cursor);
    const last = endOfMonth(cursor);
    // Pad to Monday
    let pad = (first.getDay() + 6) % 7;
    for (let i = 0; i < pad; i++) arr.push(null);
    for (let d = 1; d <= last.getDate(); d++) arr.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));
    return arr;
  }, [cursor]);

  const effective = (dateStr) => {
    if (pending[dateStr]) return pending[dateStr];
    return overrides[dateStr] || null;
  };

  const isPast = (d) => {
    if (!d) return false;
    const td = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    return d < td;
  };

  const onDayClick = (d) => {
    if (!d) return;
    const ds = ymd(d);
    if (isPast(d) || bookedDates.has(ds)) return;
    const cur = effective(ds);
    if (bulkMode === "toggle") {
      const wasAvailable = !cur || cur.available !== false;
      setPending((p) => ({
        ...p,
        [ds]: { date: ds, price: cur?.price ?? null, available: !wasAvailable },
      }));
    } else {
      setPending((p) => ({
        ...p,
        [ds]: { date: ds, price: Number(bulkPrice), available: cur?.available !== false },
      }));
    }
  };

  const clearPending = () => setPending({});

  const save = async () => {
    const list = Object.values(pending);
    if (!list.length) return;
    setSaving(true);
    try {
      await api.put(`/listings/${listingId}/calendar`, { overrides: list });
      toast.success(`${list.length} date(s) mises à jour`);
      setPending({});
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-black/10 bg-white p-6">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <button data-testid="cal-prev" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} className="p-2 border border-black/15 hover:border-[#0A0A0A]">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="font-display text-xl font-bold min-w-[170px] text-center">
            {MONTHS[cursor.getMonth()]} {cursor.getFullYear()}
          </div>
          <button data-testid="cal-next" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} className="p-2 border border-black/15 hover:border-[#0A0A0A]">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="cal-mode-toggle"
            onClick={() => setBulkMode("toggle")}
            className={`text-xs uppercase tracking-[0.15em] font-bold px-3 py-2 border ${bulkMode === "toggle" ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-black/15"}`}
          >Bloquer / débloquer</button>
          <button
            data-testid="cal-mode-price"
            onClick={() => setBulkMode("price")}
            className={`text-xs uppercase tracking-[0.15em] font-bold px-3 py-2 border ${bulkMode === "price" ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-black/15"}`}
          >Définir un prix</button>
          {bulkMode === "price" && (
            <input
              data-testid="cal-bulk-price"
              type="number"
              min={0}
              value={bulkPrice}
              onChange={(e) => setBulkPrice(e.target.value)}
              placeholder="€/nuit"
              className="border border-black/15 px-3 py-2 w-24 text-sm font-bold"
            />
          )}
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1 text-xs uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">
        {WEEKDAYS.map((w, i) => <div key={i} className="text-center py-2">{w}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {days.map((d, i) => {
          if (!d) return <div key={i} className="aspect-square" />;
          const ds = ymd(d);
          const past = isPast(d);
          const booked = bookedDates.has(ds);
          const eff = effective(ds);
          const isPending = !!pending[ds];
          const isUnavailable = eff && eff.available === false;
          const price = eff && eff.price != null ? eff.price : basePrice;
          const disabled = past || booked;
          return (
            <button
              key={i}
              data-testid={`cal-day-${ds}`}
              onClick={() => onDayClick(d)}
              disabled={disabled}
              className={`aspect-square border text-left p-1.5 relative transition-colors ${
                disabled
                  ? "bg-neutral-100 border-neutral-200 text-neutral-300 cursor-not-allowed"
                  : isUnavailable
                  ? "bg-neutral-900 text-white border-neutral-900 hover:bg-neutral-800"
                  : "bg-white border-black/15 hover:border-[#0A0A0A]"
              } ${isPending ? "ring-2 ring-[#C9986A] ring-offset-1" : ""}`}
            >
              <div className="text-xs font-bold">{d.getDate()}</div>
              {!disabled && !isUnavailable && (
                <div className="text-[10px] mt-1 opacity-70">{price}€</div>
              )}
              {booked && <div className="text-[8px] mt-1 uppercase tracking-wider text-neutral-400">Réservé</div>}
              {isUnavailable && <div className="text-[8px] mt-1 uppercase tracking-wider">Bloqué</div>}
            </button>
          );
        })}
      </div>

      <div className="mt-6 flex items-center justify-between flex-wrap gap-3">
        <div className="text-xs text-neutral-500 flex items-center gap-4 flex-wrap">
          <span><span className="inline-block w-3 h-3 bg-white border border-black/15 mr-1.5 align-middle" /> disponible</span>
          <span><span className="inline-block w-3 h-3 bg-neutral-900 mr-1.5 align-middle" /> bloqué</span>
          <span><span className="inline-block w-3 h-3 bg-neutral-100 border border-neutral-200 mr-1.5 align-middle" /> passé / réservé</span>
          <span className="text-[#C9986A] font-bold">orange = à enregistrer</span>
        </div>
        <div className="flex items-center gap-2">
          {Object.keys(pending).length > 0 && (
            <button data-testid="cal-cancel" onClick={clearPending} className="text-sm font-semibold underline">Annuler</button>
          )}
          <button
            data-testid="cal-save"
            onClick={save}
            disabled={saving || !Object.keys(pending).length}
            className="bg-[#0A0A0A] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#1B2A49] disabled:opacity-40 flex items-center gap-2"
          >
            <Save className="w-4 h-4" /> Enregistrer {Object.keys(pending).length > 0 && `(${Object.keys(pending).length})`}
          </button>
        </div>
      </div>

      {/* iCal sync panel */}
      <div className="mt-8 pt-6 border-t border-black/10">
        <div className="flex items-center gap-2 mb-4">
          <Link2 className="w-4 h-4" />
          <div className="font-display text-lg font-bold">Synchronisation iCal</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">Exporter (vers Airbnb / Booking / Abritel)</div>
            <div className="flex gap-2 items-center">
              <input
                data-testid="ical-export-url"
                readOnly
                value={exportUrl}
                className="flex-1 border border-black/15 px-3 py-2 text-xs bg-neutral-50 font-mono truncate"
              />
              <button
                data-testid="ical-copy-btn"
                onClick={copyExport}
                disabled={!exportUrl}
                title="Copier"
                className="p-2 border border-black/15 hover:border-[#0A0A0A] disabled:opacity-40"
              >
                <Copy className="w-4 h-4" />
              </button>
              {exportUrl && (
                <a href={exportUrl} target="_blank" rel="noreferrer" className="p-2 border border-black/15 hover:border-[#0A0A0A]" title="Ouvrir">
                  <ExternalLink className="w-4 h-4" />
                </a>
              )}
              <button
                data-testid="ical-rotate-btn"
                onClick={rotateToken}
                disabled={!icalInfo}
                title="Régénérer le lien"
                className="p-2 border border-black/15 hover:border-[#991B1B] hover:text-[#991B1B] disabled:opacity-40"
              >
                <RotateCw className="w-4 h-4" />
              </button>
            </div>
            <p className="text-[10px] text-neutral-500 mt-2 leading-relaxed">
              Copiez ce lien dans Airbnb → Calendrier → Synchroniser des calendriers. Vos réservations MSB y apparaîtront automatiquement.<br />
              Le bouton <RotateCw className="w-3 h-3 inline -mt-0.5" /> régénère un nouveau lien et invalide l'ancien.
            </p>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2 flex items-center justify-between">
              <span>Importer (depuis Airbnb / Booking / Abritel)</span>
              {icalInfo?.last_synced_at && (
                <span className="normal-case tracking-normal font-normal text-[10px] text-neutral-400">
                  Dernière synchro&nbsp;: {formatLastSync(icalInfo.last_synced_at)}
                </span>
              )}
            </div>
            <textarea
              data-testid="ical-import-urls"
              value={icalUrls}
              onChange={(e) => setIcalUrls(e.target.value)}
              rows={3}
              placeholder="https://www.airbnb.fr/calendar/ical/XXXX.ics&#10;https://admin.booking.com/.../YYYY.ics"
              className="w-full border border-black/15 px-3 py-2 text-xs font-mono"
            />
            <button
              data-testid="ical-sync-btn"
              onClick={syncIcal}
              disabled={syncing}
              className="mt-2 bg-[#0A0A0A] text-white px-4 py-2 text-sm font-semibold hover:bg-[#1B2A49] disabled:opacity-40 flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} /> Synchroniser maintenant
            </button>
            <p className="text-[10px] text-neutral-500 mt-2 leading-relaxed">
              Un lien par ligne. Les dates réservées sur ces plateformes seront automatiquement bloquées chez nous.
              <br />Re-synchronisation automatique <strong>toutes les heures</strong>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
